import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  DocumentTextIcon,
  UsersIcon,
  CommandLineIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useLayout, getDensityClasses } from '../contexts/LayoutContext';
import { useHeader } from '../contexts/HeaderContext';

import { MfaNotificationBanner } from './MfaNotificationBanner';
import { UserMenu } from './UserMenu';
import clsx from 'clsx';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  children?: NavItem[];
}

const getNavigation = (user?: { role?: string; permissions?: string[] }): NavItem[] => {
  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  ];

  // Only show Clients tab for non-client users (Client Admins SHOULD see it)
  const isRestrictedClient = user?.role === 'client' || user?.role === 'CLIENT_USER';
  if (!isRestrictedClient) {
    navigation.push({ name: 'Clients', href: '/clients', icon: BuildingOfficeIcon });
  }

  navigation.push({ name: 'Evaluations', href: '/assessments', icon: ClipboardDocumentListIcon });

  // Check permissions instead of roles
  const hasUsersReadPermission = user?.permissions?.includes('users:read') ||
    user?.permissions?.includes('admin:full') ||
    user?.role === 'super_admin' ||
    user?.role === 'SUPER_ADMIN';

  if (hasUsersReadPermission) {
    navigation.push({ name: 'Users', href: '/users', icon: UsersIcon });
  }

  // Admin Section
  const adminChildren: NavItem[] = [];

  // Add Questionnaire Templates menu only for SuperAdmins and Admins (who have questionnaires:write)
  const hasQuestionnairesWritePermission = user?.permissions?.includes('questionnaires:write') ||
    user?.permissions?.includes('admin:full');

  if (hasQuestionnairesWritePermission) {
    adminChildren.push({ name: 'Questionnaire Templates', href: '/questionnaires', icon: DocumentTextIcon });
  }

  // Add Email Delivery menu only for Super Admins
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'SUPER_ADMIN' ||
    user?.permissions?.includes('admin:full');

  if (isSuperAdmin) {
    adminChildren.push({ name: 'Email Delivery', href: '/email-testing', icon: EnvelopeIcon });
  }

  // Only add Admin menu if there are children
  if (adminChildren.length > 0) {
    navigation.push({
      name: 'Admin',
      icon: CommandLineIcon,
      children: adminChildren
    });
  }

  return navigation;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({ Admin: true }); // Default Admin to expanded for visibility
  const { user, isMasquerading, stopMasquerade } = useAuth();

  const { isConnected } = useSocket();
  const { title: headerTitle } = useHeader();
  const { density } = useLayout();
  const location = useLocation();
  const densityClasses = getDensityClasses(density);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="h-screen flex overflow-hidden bg-neutral-100 touch-manipulation">
      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-0 z-40 md:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div className="fixed inset-0 bg-neutral-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative h-full w-[50vw] max-w-[50vw] flex flex-col bg-white shadow-xl">
          <div className="absolute top-2 right-2">
            <button
              type="button"
              className="flex items-center justify-center h-8 w-8 rounded-full bg-primary-800 text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <SidebarContent onNavigate={() => setSidebarOpen(false)} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Masquerade Banner */}
        {isMasquerading && (
          <div className="bg-indigo-600 px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between flex-wrap">
              <div className="w-0 flex-1 flex items-center">
                <span className="flex p-2 rounded-lg bg-indigo-800">
                  <EyeIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </span>
                <p className="ml-3 font-medium text-white truncate">
                  <span className="md:hidden">Masquerading as {user?.firstName}</span>
                  <span className="hidden md:inline">
                    You are currently masquerading as {user?.firstName} {user?.lastName}.
                  </span>
                </p>
              </div>
              <div className="order-3 mt-2 flex-shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
                <button
                  onClick={stopMasquerade}
                  className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50"
                  title="End masquerade session and return to admin"
                >
                  Return to Admin
                </button>
              </div>
            </div>
          </div>
        )}
        {/* MFA Notification Banner - Above header */}
        <MfaNotificationBanner />
        {/* Top navigation */}
        <div className="relative z-10 flex-shrink-0 flex h-16 md:h-20 bg-gradient-to-r from-primary-600 to-primary-700 shadow-lg">
          <button
            type="button"
            className="px-3 sm:px-4 border-r border-primary-500 text-white hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-300 md:hidden transition-colors duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <div className="flex-1 px-3 sm:px-4 flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <span className="block truncate text-sm sm:text-base font-semibold text-white">
                {headerTitle}
              </span>
            </div>
            <div className="ml-2 flex items-center gap-2 sm:gap-3 md:ml-6">

              {/* Connection status */}
              <div className="flex items-center rounded-full bg-primary-500/40 px-2 py-1">
                <div className={clsx(
                  'w-2 h-2 rounded-full',
                  isConnected ? 'bg-success-400' : 'bg-error-400'
                )} />
                <span className="hidden sm:inline ml-1.5 text-xs text-primary-100">
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>

              {/* Notifications */}
              <button
                type="button"
                className="hidden sm:inline-flex bg-primary-500 p-1 rounded-full text-white hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-300 transition-colors duration-200"
              >
                <BellIcon className="h-6 w-6" />
              </button>

              {/* User Menu with MFA options */}
              <div className="ml-3 relative">
                <UserMenu />
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className={densityClasses.container}>
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );

  function SidebarContent({ onNavigate }: { onNavigate?: () => void } = {}) {
    const toggleMenu = (name: string) => {
      setExpandedMenus(prev => ({ ...prev, [name]: !prev[name] }));
    };

    return (
      <div className="flex flex-col h-0 flex-1 border-r border-neutral-200 bg-white">
        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-6 py-2.5 bg-primary-800 h-20">
            <Link to="/dashboard" className="flex items-center" onClick={onNavigate}>
              <img
                src="/assets/logo-white.svg"
                alt="CAPRI Compass Logo"
                className="h-[60px] w-auto"
              />
            </Link>
          </div>
          <nav className="mt-5 flex-1 px-2 space-y-1">
            {getNavigation(user || undefined).map((item) => {
              if (item.children) {
                // Parent Menu Item
                const isExpanded = expandedMenus[item.name];
                // Check if any child is active
                const isChildActive = item.children.some(child => location.pathname === child.href);
                const isActive = isExpanded || isChildActive; // Keep simplified for visual style

                return (
                  <div key={item.name} className="space-y-1">
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className={clsx(
                        'w-full group flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                        isChildActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-700'
                      )}
                    >
                      <div className="flex items-center">
                        <item.icon
                          className={clsx(
                            'mr-3 flex-shrink-0 h-5 w-5 transition-colors duration-200',
                            isChildActive ? 'text-primary-600' : 'text-neutral-400 group-hover:text-primary-600'
                          )}
                        />
                        {item.name}
                      </div>
                      {isExpanded ? (
                        <ChevronDownIcon className="h-4 w-4 text-neutral-400" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4 text-neutral-400" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="space-y-1 pl-11">
                        {item.children.map((child) => {
                          const isChildActive = location.pathname === child.href;
                          return (
                            <Link
                              key={child.name}
                              to={child.href || '#'}
                              onClick={onNavigate}
                              className={clsx(
                                'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                                isChildActive
                                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-200'
                                  : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-700'
                              )}
                            >
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Standard Menu Item
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href || '#'}
                  onClick={onNavigate}
                  className={clsx(
                    'group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                    isActive
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-200'
                      : 'text-neutral-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:text-primary-700 hover:shadow-sm'
                  )}
                >
                  <item.icon
                    className={clsx(
                      'mr-3 flex-shrink-0 h-5 w-5 transition-colors duration-200',
                      isActive ? 'text-white' : 'text-neutral-400 group-hover:text-primary-600'
                    )}
                  />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex-shrink-0 flex border-t border-primary-100 p-4 bg-gradient-to-r from-primary-50 to-primary-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mr-3">
              <span className="text-white font-semibold text-sm">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
            <div>
              <div className="text-sm font-medium text-neutral-800">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="text-xs text-neutral-600">{user?.email}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default Layout;
